import { useRef } from 'react';
import { logMyEvent } from '../firebase'; 
import { getFFmpeg } from './services/ffmpeg/core';
import { processNoiseRemoval } from './services/ffmpeg/noiseService';
import { processVoiceEnhance } from './services/ffmpeg/enhanceService';
import { extractAudio, mergeAudioVideoFiles, convertWebmToMp4, overlayCommentOnVideo } from './services/ffmpeg/videoService';

export const useAudioEngine = (audioRef, canvasRef) => {
  const audioCtxRef = useRef(null);
  const analyzerRef = useRef(null);
  const sourceRef = useRef(null);
  const audioDestRef = useRef(null);
  const chunksRef = useRef([]);

  // Initialization for Visualizer
  const init = () => {
    if (audioRef?.current && !audioCtxRef.current) {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioCtxRef.current = new AudioContext();
      sourceRef.current = audioCtxRef.current.createMediaElementSource(audioRef.current);
      analyzerRef.current = audioCtxRef.current.createAnalyser();
      analyzerRef.current.fftSize = 256;
      audioDestRef.current = audioCtxRef.current.createMediaStreamDestination();
      
      sourceRef.current.connect(analyzerRef.current);
      analyzerRef.current.connect(audioCtxRef.current.destination);
      sourceRef.current.connect(audioDestRef.current);
    }
    if (audioCtxRef.current?.state === 'suspended') audioCtxRef.current.resume();
    return { analyzer: analyzerRef.current, destination: audioDestRef.current };
  };

  // --- TOOL 1: NOISE REMOVAL ---
  const removeAudioNoise = async (audioFile, method, isExportingSetter, onProgress) => {
    isExportingSetter(true);
    const ffmpeg = await getFFmpeg();
    ffmpeg.on('progress', ({ progress }) => onProgress?.(Math.round(progress * 100)));
    
    const resultUrl = await processNoiseRemoval(ffmpeg, audioFile, method);
    isExportingSetter(false);
    return resultUrl;
  };

  // --- TOOL 2: VOICE ENHANCE ---
  const enhanceVoice = async (audioFile, isExportingSetter, onProgress) => {
    isExportingSetter(true);
    const ffmpeg = await getFFmpeg();
    ffmpeg.on('progress', ({ progress }) => onProgress?.(Math.round(progress * 100)));
    
    const resultUrl = await processVoiceEnhance(ffmpeg, audioFile);
    isExportingSetter(false);
    return resultUrl;
  };

// Nan useAudioEngine.js
const embedCommentOnVideo = async (videoFile, commentImageBlob, isExportingSetter, onProgress) => {
  isExportingSetter(true);
  const ffmpeg = await getFFmpeg();
  ffmpeg.on('progress', ({ progress }) => onProgress?.(Math.round(progress * 100)));

  try {
    // Nou jis pase Blob la, sèvis la konn rès travay la
    const resultBlob = await overlayCommentOnVideo(ffmpeg, videoFile, commentImageBlob);
    const url = URL.createObjectURL(resultBlob);
    
    const a = document.createElement('a');
    a.href = url;
    a.download = `vidabol-comment-${Date.now()}.mp4`;
    a.click();
    
    return url;
  } catch (error) {
    console.error("Embed Error:", error);
    alert("Failed to embed comment.");
  } finally {
    isExportingSetter(false);
  }
};

  // --- TOOL 3: VIDEO TO AUDIO ---
  const extractAudioFromVideo = async (videoFile, format, isExportingSetter, onProgress) => {
    isExportingSetter(true);
    logMyEvent('extract_audio_start', { format, file_size: videoFile.size });
    
    const ffmpeg = await getFFmpeg();
    ffmpeg.on('progress', ({ progress }) => onProgress?.(Math.round(progress * 100)));

    try {
      const blob = await extractAudio(ffmpeg, videoFile, format);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `vidabol-audio-${Date.now()}.${format}`;
      a.click();
      logMyEvent('extract_audio_success', { format });
    } catch (error) {
      logMyEvent('extract_audio_error', { error: error.message });
      alert("Extraction failed.");
    } finally {
      isExportingSetter(false);
    }
  };

  // --- TOOL 4: MERGE AUDIO & VIDEO ---
  const mergeAudioVideo = async (videoFile, audioFile, isExportingSetter, onProgress) => {
    isExportingSetter(true);
    logMyEvent('merge_audio_video_start', { video_size: videoFile.size, audio_size: audioFile.size });

    const ffmpeg = await getFFmpeg();
    ffmpeg.on('progress', ({ progress }) => onProgress?.(Math.round(progress * 100)));

    try {
      const blob = await mergeAudioVideoFiles(ffmpeg, videoFile, audioFile);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `vidabol-merged-${Date.now()}.mp4`;
      a.click();
      logMyEvent('merge_audio_video_success', { format: 'mp4' });
    } catch (error) {
      logMyEvent('merge_audio_video_error', { error: error.message });
      alert("Merge failed.");
    } finally {
      isExportingSetter(false);
    }
  };

  // --- TOOL 5: EXPORT (VISUALIZER TO VIDEO) ---
  const exportVideo = async (format, isExportingSetter, onStartVisuals, onProgress) => {
    const { destination } = init();
    isExportingSetter(true);
    logMyEvent('video_export_start', { format });

    if (onStartVisuals) onStartVisuals();
    
    const audio = audioRef.current;
    audio.currentTime = 0;
    const stream = new MediaStream([
      ...canvasRef.current.captureStream(30).getTracks(),
      ...destination.stream.getTracks()
    ]);

    const recorder = new MediaRecorder(stream, { mimeType: 'video/webm' });
    chunksRef.current = [];
    recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
    
    recorder.onstop = async () => {
      try {
        const webmBlob = new Blob(chunksRef.current, { type: 'video/webm' });
        
        if (format === 'mp4') {
          const ffmpeg = await getFFmpeg();
          ffmpeg.on('progress', ({ progress }) => onProgress?.(Math.round(progress * 100)));
          const mp4Blob = await convertWebmToMp4(ffmpeg, webmBlob);
          downloadFile(mp4Blob, 'mp4');
        } else {
          downloadFile(webmBlob, 'webm');
        }
        logMyEvent('video_export_success', { format });
      } catch (error) {
        logMyEvent('video_export_error', { format, error: error.message });
        alert("Export failed.");
      } finally {
        isExportingSetter(false);
      }
    };

    const downloadFile = (blob, ext) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `vidabol-export-${Date.now()}.${ext}`;
      a.click();
    };

    recorder.start();
    audio.play();
    audio.onended = () => recorder.stop();
  };

  // --- HELPER: DURATION VALIDATION ---
  const validateFileDuration = (file, maxMinutes = 15) => {
    return new Promise((resolve) => {
      const isVideo = file.type.startsWith('video');
      const element = document.createElement(isVideo ? 'video' : 'audio');
      element.preload = 'metadata';
      element.onloadedmetadata = () => {
        const maxSeconds = maxMinutes * 60;
        resolve({ 
          isValid: element.duration <= maxSeconds, 
          msg: `File exceeds the ${maxMinutes} minute limit.` 
        });
      };
      element.onerror = () => resolve({ isValid: false, msg: "Invalid file format." });
      element.src = URL.createObjectURL(file);
    });
  };

  return { 
    init, 
    exportVideo, 
    extractAudioFromVideo, 
    mergeAudioVideo, 
    removeAudioNoise, 
    enhanceVoice, 
    validateFileDuration,
    embedCommentOnVideo
  };
};